import type { ChangeEvent } from "react";
import type React from "react";

import { Chip, createFilterOptions } from "@mui/material";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import styled from "styled-components";

import { MUIAutocomplete } from "shared/atoms/inputs/MUIAutocomplete";
import { getFieldOptionKey } from "utils/medicalNotes";

interface Props {
  readonly error?: string;
  readonly label?: string;
  readonly name: string;
  readonly disabled?: boolean;
  readonly required?: boolean;
  readonly options: string[];
  // eslint-disable-next-line @typescript-eslint/ban-types
  readonly onChange?: (event: ChangeEvent<{}>) => void;
}

export const MUIMultiselectAutocomplete: React.FC<Props> = ({
  disabled,
  error,
  label,
  name,
  options,
  required,
  onChange,
}) => {
  const { setValue } = useFormContext();
  const { t } = useTranslation();

  return (
    <MUIAutocomplete
      disableCloseOnSelect
      disabled={disabled}
      error={error}
      label={label}
      name={name}
      options={options}
      required={required}
      onChange={(event, value) => {
        setValue(name, value, {
          shouldDirty: true,
          shouldValidate: true,
        });
        if (onChange) onChange(event);
      }}
      filterOptions={createFilterOptions({
        stringify: (option: string) => {
          // FIXME: type translation
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore
          return t(getFieldOptionKey(name, option)) as string;
        },
      })}
      renderOption={(props, option) => (
        // FIXME: type translation
        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        <MenuItem label={t(getFieldOptionKey(name, option))} name={name} option={option} {...props} />
      )}
      renderTag={(valueArray, getTagProps) =>
        valueArray.map((value, i) => (
          <SelectedItem
            key={value}
            // FIXME: type translation
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            label={t(getFieldOptionKey(name, value))}
            name={name}
            option={value}
            tagProps={getTagProps({ index: i })}
          />
        ))
      }
    />
  );
};

interface MenuItemProps {
  readonly className?: string;
  readonly label?: string;
  readonly name: string;
  readonly option: string;
  readonly children?: React.ReactNode;
}

interface SelectedItemProp {
  readonly label: string;
  readonly name: string;
  readonly option: string;
  readonly tagProps: Record<string, unknown>;
}

export const MenuItem: React.FC<MenuItemProps> = ({
  className,
  label,
  name,
  option,
  children,
  ...props
}): JSX.Element => (
  <ItemContainer className={className} {...props}>
    <DescriptionContainer
      data-testid={`multiselect-dropdown-${name}-option-${option}`}
      onClick={e => e.preventDefault()}
    >
      {label}
      {children}
    </DescriptionContainer>
  </ItemContainer>
);

export const SelectedItem: React.FC<SelectedItemProp> = ({ label, name, option, tagProps }) => (
  <StyledChip
    variant="outlined"
    {...tagProps}
    label={
      <ItemContainer>
        <DescriptionContainer data-testid={`multiselect-dropdown-${name}-selected-option-${option}`}>
          {label}
        </DescriptionContainer>
      </ItemContainer>
    }
  />
);

const StyledChip = styled(Chip)`
  &.MuiChip-root {
    border-radius: ${props => props.theme.borderRadius.basic};
    margin: ${props => props.theme.spacing.S_5};
    margin-left: 0;
    border: none;
    background-color: ${props => props.theme.colors.redesign.b10};

    .MuiChip-label {
      padding: 0 ${props => props.theme.spacing.S_5};
    }
    .MuiChip-deleteIcon {
      pointer-events: all;
      color: ${props => props.theme.colors.redesign.db50};
    }
  }
`;

const ItemContainer = styled.li`
  align-items: center;
  display: flex;
  color: ${props => props.theme.colors.redesign.db100};
  font-size: 14px;
  font-weight: 500;
  height: 40px;
  padding-right: 5px;
`;

const DescriptionContainer = styled.div`
  padding: 2px 4px;
  white-space: nowrap;
  ${props => props.theme.belowBreakpoint} {
    white-space: normal;
  }
`;
